import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Station, DayOfWeek } from '@/types';
import { formatAddress, formatOpeningTime } from '@/utils/format';
import { AMENITY_LABELS } from '@/constants';
import { DAYS_OF_WEEK as DAYS_LIST } from '@/types';
import { clsx } from 'clsx';

interface StationInfoProps {
  station: Station;
  className?: string;
}

export function StationInfo({ station, className }: StationInfoProps) {
  const [showAllHours, setShowAllHours] = useState(false);
  const today = getCurrentDay();
  const todayHours = station.openingHours[today];

  return (
    <div className={clsx('space-y-5', className)}>
      {/* Address */}
      <section>
        <h3 className="text-sm font-medium text-gray-700 mb-2">Address</h3>
        <p className="text-gray-600 text-sm leading-relaxed">
          {formatAddress(station.address)}
        </p>
      </section>

      {/* Opening hours */}
      <section>
        <h3 className="text-sm font-medium text-gray-700 mb-2">Opening Hours</h3>

        {/* Today's hours - always visible */}
        <div className="bg-brand-50 rounded-lg p-3 mb-2">
          <div className="flex items-center justify-between">
            <span className="font-medium text-brand-700">Today</span>
            <span className="font-medium text-brand-700">
              {todayHours.is24Hours
                ? '24 hours'
                : `${formatOpeningTime(todayHours.open)} - ${formatOpeningTime(todayHours.close)}`}
            </span>
          </div>
        </div>

        {/* Expand toggle */}
        <button
          type="button"
          onClick={() => setShowAllHours(!showAllHours)}
          className="text-sm text-brand-600 hover:text-brand-700 flex items-center gap-1"
        >
          {showAllHours ? 'Hide full schedule' : 'Show full schedule'}
          <motion.svg
            animate={{ rotate: showAllHours ? 180 : 0 }}
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </motion.svg>
        </button>

        {/* Full week schedule */}
        <AnimatePresence>
          {showAllHours && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="pt-3 space-y-1 text-sm">
                {DAYS_LIST.map((day) => {
                  const hours = station.openingHours[day];
                  const isToday = day === today;

                  return (
                    <div
                      key={day}
                      className={clsx(
                        'flex justify-between py-1 px-2 rounded',
                        isToday && 'bg-brand-50 font-medium'
                      )}
                    >
                      <span className="capitalize text-gray-700">{day}</span>
                      <span className={isToday ? 'text-brand-700' : 'text-gray-600'}>
                        {hours.is24Hours
                          ? '24 hours'
                          : `${formatOpeningTime(hours.open)} - ${formatOpeningTime(hours.close)}`}
                      </span>
                    </div>
                  );
                })}

                {/* Bank holiday */}
                <div className="flex justify-between pt-2 mt-2 border-t border-gray-100 text-gray-500">
                  <span>Bank holidays</span>
                  <span>
                    {station.bankHoliday.is24Hours
                      ? '24 hours'
                      : `${formatOpeningTime(station.bankHoliday.open)} - ${formatOpeningTime(station.bankHoliday.close)}`}
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {/* Amenities */}
      {station.amenities.length > 0 && (
        <section>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Amenities</h3>
          <div className="flex flex-wrap gap-2">
            {station.amenities.map((amenity) => (
              <span
                key={amenity}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-full text-xs text-gray-700"
              >
                <AmenityIcon amenity={amenity} className="w-3.5 h-3.5" />
                {AMENITY_LABELS[amenity] || amenity}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Station type badges */}
      {(station.isMotorway || station.isSupermarket) && (
        <section>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Station Type</h3>
          <div className="flex flex-wrap gap-2">
            {station.isMotorway && (
              <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                Motorway Services
              </span>
            )}
            {station.isSupermarket && (
              <span className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Supermarket
              </span>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function getCurrentDay(): DayOfWeek {
  const days: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[new Date().getDay()] ?? 'monday';
}

// Simple icon component for amenities
function AmenityIcon({ amenity, className }: { amenity: string; className?: string }) {
  const icons: Record<string, React.ReactNode> = {
    car_wash: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
      </svg>
    ),
    customer_toilets: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
  };

  return icons[amenity] || (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}
